import { z } from "zod";
import { StudentSchema } from "./User";

export enum MessageActions {
    Copy = 0,
    Reply = 1,
    Forward = 2,
    Delete = 3
}

export enum RoomOverflowOptions {
    REMOVE = 'REMOVE',
    EDIT = 'EDIT', 
    BLOCK = 'BLOCK',
    LEAVE = 'LEAVE',
    CLOSE = 'CLOSE' // Added CLOSE option
}

export const CHAT_CATEGORIES = [
    { label: "همه", value: "ALL", key: '0' },
    { label: "شخصی", value: "private", key: '26' },
    { label: "کانال", value: "channel", key: '52' },
    { label: "گروه", value: "group", key: '77' }
]

export const LoginSchema = z.object({
    chat_id: z.string(),
    token: z.string(),
    extras: z.array(z.object({
        id: z.string(),
        name: z.string(),
        pinned: z.boolean().optional(),
        type: z.enum(["private", "channel", "group"])
    })),
    counselor_chat_id: z.string().optional().nullable(),
    // counselor_user_id: z.number().optional().nullable(),
    counselor_channel_id: z.string().optional().nullable()
})

export const LastMessageSchema = z.object({
    SenderFullname: z.string(),
    Content: z.string()
})

export const MemberSchema = z.object({
    ID: z.string(),
    Avatar: z.string(),
    Fullname: z.string(),
    Status: z.enum(['offline', 'online']),
    LastActivity: z.string().or(z.date()).optional()
})

export const MessageSchema = z.object({
    ID: z.string(),
    Sign: z.string(),
    SenderID: z.string(),
    SenderFullname: z.string(),
    Content: z.string().optional().nullable(),
    ContentType: z.enum(['anouncement', 'text', 'file', 'voice', 'image', 'video']),
    SenderAvatar: z.string(),
    Status: z.enum(['sent', 'sending']),
    Seen: z.boolean(),
    CreatedAt: z.string().or(z.date()),
    ParentMessageID: z.string().optional().nullable(),
    ParentMessage: z.unknown().optional().nullable(),
    Type: z.string().optional().nullable()
})

export const RoomSchema = z.object({
    ID: z.string(),
    Name: z.string().optional().nullable(),
    Type: z.enum(['channel', 'group', 'private']),
    UnreadMessageCount: z.number(),
    LastMessage: MessageSchema.optional().nullable(),
    LastMessageAt: z.string().or(z.date()),
    Image: z.string().optional().nullable(),
    Description: z.string().optional().nullable(),
    Member: MemberSchema.optional().nullable()
})

export const ReceivedMessageSchema = z.object({
    Data: MessageSchema.extend({
        Room: RoomSchema
    }),
    Type: z.enum(['new_message', 'mark_as_read', 'response', 'pong'])
});

export const SentMessageSchema = MessageSchema.extend({
    Type: z.enum(['message']),
    RoomID: z.string(),
    ReceiverID: z.string().optional().nullable(),
    MessageType: z.enum(['direct', 'forwarded', 'replied']),
})

export const StudentSpecificSchema = StudentSchema.extend({
    roomData: RoomSchema.optional()
})

export const CreateGroupSchema = z.object({
    title: z.string().min(1, { message: "نام گروه الزامی است" }),
    description: z.string().min(1, { message: "توضیحات گروه الزامی است" }),
    image: z.instanceof(File, { message: "فرمت تصویر نامعتبر است" }).optional().nullable(),
});  

export const GROUP_FORM_DEFAULT_VALUE = {
    title: '',
    description: '',
    image: null
}

export const MembersResponseSchema = z.array(MemberSchema);
export const RoomResponseSchema = z.array(RoomSchema);
export const MessageResponseSchema = z.array(MessageSchema);

export type LoginType = z.infer<typeof LoginSchema>;
export type RoomType = z.infer<typeof RoomSchema>;
export type RoomResponseType = z.infer<typeof RoomResponseSchema>;
export type MessageType = z.infer<typeof MessageSchema>;
export type MessageResponseType = z.infer<typeof MessageResponseSchema>;
export type  MembersResponseType = z.infer<typeof MembersResponseSchema>;
export type ReceivedMessageType = z.infer<typeof ReceivedMessageSchema>;
export type SentMessageType = z.infer<typeof SentMessageSchema>;
export type StudentSpecificType = z.infer<typeof StudentSpecificSchema>;

export type MessageQueueType = {
    message: MessageType;
    progress: number;
    file?: File | null;
}

export type ChatRoomDataType = {
    room?: RoomType | null;
    user_id?: number | null;
}

export type FileTransferDataType = {
    ID: string;
    file?: File | null;
};

export type FileUploadProgressDataType = { 
    ID: string;
    percent: number;
};

export type RoomSelectionType = {
    name: string;
    roomID?: string | null;
    chatID?: string | null;
}

export type CreateGroupFormType = z.infer<typeof CreateGroupSchema>;
